import {
  adminChangePasswordInput,
  adminResetPinInput,
  api,
  memberUpdateInput,
  queryKeys,
  type AdminChangePasswordInput,
  type AdminResetPinInput,
  type MemberUpdateInput,
} from '@alpha/api-client';
import { zodResolver } from '@hookform/resolvers/zod';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { useState } from 'react';
import { useForm } from 'react-hook-form';
import { useParams } from 'react-router-dom';
import { PageHeader } from '../../components/PageHeader';
import { formatDate, statusTone } from '../../lib/format';
import { Alert, Button, Card, Chip, FormGroup, Input, Modal, Spinner, StatCard, StatCards } from '../../ui';

export function MemberDetail() {
  const { id = '' } = useParams();
  const queryClient = useQueryClient();
  const [editOpen, setEditOpen] = useState(false);

  const { data: member, isLoading } = useQuery({
    queryKey: queryKeys.member(id),
    queryFn: () => api.users.show(id).then((r) => r.data),
    enabled: !!id,
  });

  const invalidate = () => queryClient.invalidateQueries({ queryKey: queryKeys.member(id) });

  /**
   * Wired to POST /admin/members/{id}/toggle, which flips the ACCOUNT status
   * (active/inactive) — a different concept from package activation below.
   * Labelled "Block"/"Unblock" so it can't be confused with activatePackage.
   */
  const toggleStatus = useMutation({ mutationFn: () => api.admin.toggleStatus(id), onSuccess: invalidate });

  /** POST /admin/members/{id}/activate — grants the joining package, dispatches referral/level income. */
  const activatePackage = useMutation({
    mutationFn: () => api.admin.activateMember(id),
    onSuccess: invalidate,
  });

  /**
   * Impersonation hands the admin a user session and redirects into the user
   * panel, which renders the impersonation banner for the whole session — the
   * admin sees the exact same dashboard, sidebar, and every page a real
   * member would. Was hardcoded to http://localhost:5174/ (a local dev
   * leftover), which is a dead address on production — impersonate silently
   * "worked" (session created) but sent the admin's browser nowhere useful.
   * Matches the production user-panel URL already hardcoded elsewhere in this
   * codebase (see Dashboard.tsx's ReferralLinkCard in the user app).
   */
  const impersonate = useMutation({
    mutationFn: () => api.admin.impersonate(id),
    onSuccess: () => window.location.assign('https://app.alphaone.group/'),
  });

  const editForm = useForm<MemberUpdateInput>({
    resolver: zodResolver(memberUpdateInput),
    defaultValues: { name: '', email: '', phone: '', wallet_address: '' },
  });

  const updateMember = useMutation({
    mutationFn: (values: MemberUpdateInput) => api.users.update(id, values),
    onSuccess: () => {
      invalidate();
      setEditOpen(false);
    },
  });

  const openEdit = () => {
    if (!member) return;
    editForm.reset({
      name: member.name,
      email: member.email ?? '',
      phone: member.phone ?? '',
      wallet_address: member.wallet_address ?? '',
    });
    setEditOpen(true);
  };

  // Passwords/PINs are one-way hashed server-side — these forms can only SET a
  // new value, never show the current one.
  const passwordForm = useForm<AdminChangePasswordInput>({
    resolver: zodResolver(adminChangePasswordInput),
    defaultValues: { password: '', password_confirmation: '' },
  });
  const resetPassword = useMutation({
    mutationFn: (values: AdminChangePasswordInput) => api.admin.changeMemberPassword(id, values),
    onSuccess: () => passwordForm.reset(),
  });

  const pinForm = useForm<AdminResetPinInput>({
    resolver: zodResolver(adminResetPinInput),
    defaultValues: { transaction_pin: '', transaction_pin_confirmation: '' },
  });
  const resetPin = useMutation({
    mutationFn: (values: AdminResetPinInput) => api.admin.resetMemberPin(id, values),
    onSuccess: () => pinForm.reset(),
  });

  if (isLoading) return <Spinner />;
  if (!member) return null;

  return (
    <>
      <PageHeader
        title={member.name}
        description={`${member.user_code} · joined ${formatDate(member.created_at)}`}
        actions={
          <>
            <Button variant="outline-gold" onClick={openEdit}>
              Edit
            </Button>
            <Button
              variant={member.status === 'active' ? 'danger' : 'outline-gold'}
              onClick={() => toggleStatus.mutate()}
            >
              {member.status === 'active' ? 'Block' : 'Unblock'}
            </Button>
            {!member.is_activated ? (
              <Button variant="gold" onClick={() => activatePackage.mutate()} disabled={activatePackage.isPending}>
                {activatePackage.isPending ? 'Activating…' : 'Activate Package'}
              </Button>
            ) : null}
            <Button variant="gold" onClick={() => impersonate.mutate()}>
              Impersonate
            </Button>
          </>
        }
      />

      {activatePackage.isSuccess ? (
        <Alert tone="success">Member activated. Referral and level income dispatched.</Alert>
      ) : null}
      {activatePackage.error ? <Alert tone="danger">{(activatePackage.error as Error).message}</Alert> : null}

      <StatCards>
        <StatCard label="Status" value={<Chip tone={statusTone(member.status)}>{member.status}</Chip>} />
        <StatCard label="Activated" value={member.is_activated ? 'Yes' : 'No'} sub={formatDate(member.activated_at)} />
        <StatCard label="KYC" value={<Chip tone={statusTone(member.kyc_status)}>{member.kyc_status}</Chip>} />
        <StatCard label="Rank" value={member.rank_code ?? '—'} />
      </StatCards>

      <Card>
        <h3 className="card-title">Account</h3>
        <dl className="detail-list">
          <div>
            <dt>Email</dt>
            <dd>{member.email}</dd>
          </div>
          <div>
            <dt>Phone</dt>
            <dd>{member.phone ?? '—'}</dd>
          </div>
          <div>
            <dt>Sponsor</dt>
            <dd>{member.sponsor_code ?? '—'}</dd>
          </div>
          <div>
            <dt>Wallet address</dt>
            <dd>{member.wallet_address ?? '—'}</dd>
          </div>
          <div>
            <dt>Two-factor</dt>
            <dd>{member.two_factor_enabled ? 'Enabled' : 'Disabled'}</dd>
          </div>
        </dl>
      </Card>

      <Card>
        <h3 className="card-title">Reset Login Password</h3>
        <p className="muted">
          Login passwords are one-way hashed — there is no way to view the current password, only set a new one.
        </p>
        {resetPassword.isSuccess ? <Alert tone="success">Password updated for {member.user_code}.</Alert> : null}
        {resetPassword.error ? <Alert tone="danger">{(resetPassword.error as Error).message}</Alert> : null}
        <form onSubmit={passwordForm.handleSubmit((values) => resetPassword.mutate(values))} noValidate>
          <FormGroup label="New password" error={passwordForm.formState.errors.password?.message}>
            <Input type="password" autoComplete="new-password" {...passwordForm.register('password')} />
          </FormGroup>
          <FormGroup
            label="Confirm new password"
            error={passwordForm.formState.errors.password_confirmation?.message}
          >
            <Input type="password" autoComplete="new-password" {...passwordForm.register('password_confirmation')} />
          </FormGroup>
          <Button type="submit" variant="gold" disabled={resetPassword.isPending}>
            {resetPassword.isPending ? 'Saving…' : 'Set new password'}
          </Button>
        </form>
      </Card>

      <Card>
        <h3 className="card-title">Reset Transaction PIN</h3>
        <p className="muted">
          The withdrawal/activation PIN is one-way hashed the same way — set a new PIN rather than viewing the
          current one.
        </p>
        {resetPin.isSuccess ? <Alert tone="success">Transaction PIN updated for {member.user_code}.</Alert> : null}
        {resetPin.error ? <Alert tone="danger">{(resetPin.error as Error).message}</Alert> : null}
        <form onSubmit={pinForm.handleSubmit((values) => resetPin.mutate(values))} noValidate>
          <FormGroup label="New transaction PIN" error={pinForm.formState.errors.transaction_pin?.message}>
            <Input type="password" inputMode="numeric" maxLength={8} {...pinForm.register('transaction_pin')} />
          </FormGroup>
          <FormGroup
            label="Confirm new PIN"
            error={pinForm.formState.errors.transaction_pin_confirmation?.message}
          >
            <Input
              type="password"
              inputMode="numeric"
              maxLength={8}
              {...pinForm.register('transaction_pin_confirmation')}
            />
          </FormGroup>
          <Button type="submit" variant="gold" disabled={resetPin.isPending}>
            {resetPin.isPending ? 'Saving…' : 'Set new PIN'}
          </Button>
        </form>
      </Card>

      <Modal
        open={editOpen}
        onClose={() => setEditOpen(false)}
        title="Edit member"
        footer={
          <>
            <Button variant="outline-gold" onClick={() => setEditOpen(false)}>
              Cancel
            </Button>
            <Button
              variant="gold"
              onClick={editForm.handleSubmit((values) => updateMember.mutate(values))}
              disabled={updateMember.isPending}
            >
              {updateMember.isPending ? 'Saving…' : 'Save changes'}
            </Button>
          </>
        }
      >
        {updateMember.error ? <Alert tone="danger">{(updateMember.error as Error).message}</Alert> : null}
        <form onSubmit={editForm.handleSubmit((values) => updateMember.mutate(values))} noValidate>
          <FormGroup label="Name" error={editForm.formState.errors.name?.message}>
            <Input {...editForm.register('name')} />
          </FormGroup>
          <FormGroup label="Email" error={editForm.formState.errors.email?.message}>
            <Input type="email" {...editForm.register('email')} />
          </FormGroup>
          <FormGroup label="Phone" error={editForm.formState.errors.phone?.message}>
            <Input {...editForm.register('phone')} />
          </FormGroup>
          <FormGroup label="Wallet address" error={editForm.formState.errors.wallet_address?.message}>
            <Input placeholder="0x…" {...editForm.register('wallet_address')} />
            <p className="muted">
              Set this to let the member log in via that wallet — used to manually link an existing account after
              the DApp registration cutover.
            </p>
          </FormGroup>
        </form>
      </Modal>
    </>
  );
}
